import React from 'react';
import I18n from 'i18n-js';
import PostalAddress from 'i18n-postal-address';
import { ChildProps } from 'react-apollo';
import { decamelizeKeys } from 'humps';
import gql from 'graphql-tag';
import classNames from 'classnames';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import {
  core_apimessages_AddressEntry as IAddressEntry,
  core_apimessages_Country as ICountry,
  CoreAddressSummary,
} from '../../schemas/rql';
import { configFields } from '../../lib/address_form_fields_helpers';

interface IExternalProps {
  address: IAddressEntry;
  hideCountry?: boolean;
  countriesOverride?: ICountry[];
  hideNameField?: boolean;
}

type IProps = ChildProps<IExternalProps, CoreAddressSummary.Query>;

/**
 * @deprecated Use [AddressSummary](../address_summary.tsx) instead to avoid nested GraphQL queries
 */
export function RQLAddressSummary({ address, data, countriesOverride, hideCountry, hideNameField }: IProps) {
  if (!data || data.loading) return null;

  const countries = countriesOverride || data?.countries?.countries;
  const configs = data?.intlAddressFormConfig?.configs;
  const countryName = countryCodeToName(address.countryCode, countries);
  const regionName = regionNameInCountry(address.countryCode, address.region, countries);
  const addressLines = mapAddressToAddressLines(address, regionName);

  return (
    <div className="formatted-address">
      <p className="formatted-address__info">
        {addressLines.map(((addressLine, index) => (
          <AddressLineDisplay
            data-address-summary__address-line
            key={`address_line_${index}`}
            line={addressLine}
            highlighted={index === 1}
          />
        )))}
        {!hideCountry &&
          <span data-address-summary__address-line>
            {countryName}
          </span>
        }
      </p>
      {isIncomplete(address, configs, hideNameField) &&
        <p className="formatted-address__incomplete">
          {I18n.t('discovery.addressBook.addressSummary.incompleteAddress')}
        </p>
      }
    </div>
  );
}

function AddressLineDisplay(props: { line: string, highlighted: boolean }) {
  const { line, highlighted } = props;

  return (
    <>
      <span className={classNames({ 'formatted-address__highlight': highlighted })}>
        {line}
      </span>
      <br />
    </>
  );
}

function mapAddressToAddressLines(address: IAddressEntry, regionName: string): string[] {
  const output = buildPostalAddress(address, regionName).output();

  if (typeof output === 'string') {
    return [output];
  }

  return output.map(line => {
    if (line.length > 1) {
      return line.join(' ');
    }

    return line[0];
  });
}

function buildPostalAddress(address: IAddressEntry, regionName: string): PostalAddress {
  const postalAddress = new PostalAddress();
  return postalAddress
    .setFirstName(address.name)
    .setAddress1(address.streetAddress)
    .setAddress2(address.extendedAddress)
    .setCity(address.locality)
    .setState(regionName)
    .setProvince(regionName)
    .setPostalCode(address.postalCode)
    .setFormat({
      country: address.countryCode,
    });
}

const findCountry = (countryCode: string, countries: ICountry[]): ICountry => {
  return countries?.find(c => c.countryCode === countryCode);
};

const countryCodeToName = (countryCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  return country && country.name;
};

const regionNameInCountry = (countryCode: string, regionCode: string, countries: ICountry[]): string => {
  const country = findCountry(countryCode, countries);
  const region = country && country.subregions.find(r => (r.code === regionCode));
  return region && region.name;
};

function isIncomplete(address: IAddressEntry, configs, hideNameField) {
  const addressFields = configFields(address.countryCode, configs);
  const decamelizedAddress = decamelizeKeys(address);

  return addressFields?.some(field => {
    if (field.fieldName === 'name' && hideNameField) {
      return false;
    }
    return !decamelizedAddress[field.fieldName] && field.required;
  });
}

const ADDRESS_SUMMARY_QUERY = gql`
  query Core_Address_Summary(
    $countriesOverride: Boolean!
    ) {
    countries @skip (if: $countriesOverride) {
      countries {
        name
        countryCode
        subregions {
          name
          code
        }
      }
    }
    intlAddressFormConfig {
      configs {
        countryCode
        fields {
          fieldName
          displayNames {
            locale
            displayName
          }
          required
        }
      }
    }
  }
`;

const withQuery = withGraphql<IExternalProps, CoreAddressSummary.Query>(
  ADDRESS_SUMMARY_QUERY,
  {
    options: ({ countriesOverride }) => {
      return {
        ssr: true,
        variables: {
          countriesOverride: countriesOverride?.length > 0,
        },
      };
    },
  },
);

/**
 * @deprecated Use [AddressSummary](../address_summary.tsx) instead to avoid nested GraphQL queries
 */
export default withQuery(RQLAddressSummary);
